/* Filtrous quick view - v1.5, 2026-08-26 (Adelaide: "there should be a quick
   view option" / "i really like this quick view button if I hover over it and
   I can scroll for the specs" / "i like this but why can't I scroll?" / "the
   description should be the length of the image and within that quick view
   there should be a scroll").
   One shared modal for peeking at a product without leaving the page (or
   losing the cart-drawer flow): image, title, SKU, price, spec bullets, the
   description from the public /products/<handle>.js, a qty stepper + Add to
   cart (variant id from the catalog index; header count + open drawer
   refreshed after), and a "View full details" link to the real PDP.
   LAYOUT / SCROLLING (v1.3): the dialog is a FIXED height - the two columns
   are exactly as tall as the product image (--fqv-h). The right column is a
   flex stack: title/SKU/price/stock pinned at the top, the qty + Add to cart
   row pinned at the bottom, and the specs + description scrolling in the
   labelled region between them (visible thin scrollbar, the rails'
   treatment). So the card never changes size product to product, the buy
   action is always in reach, and long copy is reachable by scrolling inside
   the quick view rather than being cut off.
   LEAD SENTENCE (v1.4, Adelaide: "make the first sentence of the
   description visible"): the spec bullets filled the scroll region, so the
   prose underneath them opened out of sight. The description's FIRST
   SENTENCE now leads that region - readable the moment the card opens -
   and the remainder stays under the specs, so no copy is duplicated and
   the specs keep the high position the QUICK VIEW bar was built for.
   BUY ROW (v1.5, Adelaide: "after they click it they should also be able to
   add it to cart"): the row used to render EMPTY for an item the index
   shows as out of stock - a blank bar reads as a broken card, so it now
   shows a disabled "Sold out" button. A failed add tells the two cases
   apart as well: 422 (the cart refusing) becomes a disabled Sold out,
   anything else stays "Try again".
   The two earlier attempts and why they were wrong: v1.1 clamped the
   description to four lines and nested the specs in their own pane, so the
   copy was truncated and short products scrolled nowhere; v1.2 unclamped
   everything and let the whole dialog scroll, which fixed the truncation but
   let the card grow to whatever the copy needed.
   TRIGGERS are decoupled: ANY element carrying data-fqv-handle="<handle>"
   opens the modal for that product - this asset's capture-phase click
   handler intercepts (and prevents navigation when the trigger sits inside
   a product link, as the rail-card bars do). Current triggers: the
   "QUICK VIEW" bar across each PDP rail card image (filtrous-pdp-merch.js)
   and the "Quick view" control on each cart-drawer suggestion row
   (filtrous-cart-upsell.js).
   A11Y (the filtrous-a11y.js posture): role=dialog + aria-modal, focus
   moves to the close button on open, Tab is trapped inside, Escape and
   backdrop click close, focus returns to the opener; the scrolling region is
   a labelled, keyboard-focusable region. v1.0.1: the Escape handler also
   stopPropagation()s so the cart drawer's own document-level Escape listener
   doesn't close the drawer underneath in the same keypress - Esc peels one
   layer at a time. Motion (open fade/scale) runs only under
   prefers-reduced-motion: no-preference. Sits above the cart drawer's veil
   (z 2147483000) at z 2147483400; the drawer's outside-click closer
   explicitly ignores clicks inside #fqv-veil.
   v1.0.2: most product bodies START with the spec TABLE, so a naive
   textContent produced descriptions reading "Specification Value Format...".
   The body HTML is now split: tables become spec bullets (used when the
   catalog index carries no specs for that item), everything else becomes
   the prose description.
   Data: catalog index (URL from this tag's data-index-url, falling back to
   FV3_CONFIG.indexUrl) for image/price/sku/specs; /products/<handle>.js
   for description + fallback specs, raced against a 1200ms timeout so the
   modal never waits on a slow network. OSK quote items render "Call for
   Quote" with no add row. Loaded (deferred) from
   snippets/filtrous-cart-drawer.liquid so it exists on every page; remove
   that line to revert (triggers go inert: rail bars stop rendering via
   their own guard, drawer controls no-op). */
(function () {
  'use strict';
  if (window.FV3_QUICKVIEW) return;

  var script = document.currentScript;
  var INDEX_URL = (script && script.getAttribute('data-index-url')) || (window.FV3_CONFIG && window.FV3_CONFIG.indexUrl) || '';

  var css = document.createElement('style');
  css.textContent =
    '#fqv-veil{display:none;position:fixed;inset:0;background:rgba(6,35,52,.55);z-index:2147483400;align-items:center;justify-content:center;padding:20px}' +
    '#fqv-veil.on{display:flex}' +
    /* --fqv-h: the height of the image, and therefore of the whole card */
    '#fqv{--fqv-h:352px;background:#fff;border-radius:14px;width:min(660px,94vw);max-height:90vh;overflow:hidden;box-shadow:0 24px 64px rgba(6,35,52,.35);font-family:\'DM Sans\',system-ui,-apple-system,sans-serif;color:#12222f;position:relative}' +
    '@media (max-height:520px){#fqv{--fqv-h:300px}}' +
    '@media (prefers-reduced-motion: no-preference){#fqv{transform:scale(.96);opacity:0;transition:transform .18s cubic-bezier(.22,.8,.36,1),opacity .18s}#fqv-veil.on #fqv{transform:scale(1);opacity:1}}' +
    '#fqv *{box-sizing:border-box}' +
    '#fqv-x{position:absolute;top:10px;right:12px;background:none;border:0;font-size:26px;line-height:1;color:#5c6f7e;cursor:pointer;padding:2px 6px;z-index:3}' +
    '#fqv-x:hover{color:#12222f}' +
    '.fqv-grid{display:grid;grid-template-columns:270px 1fr;height:var(--fqv-h)}' +
    '.fqv-media{padding:22px;display:flex;align-items:center;justify-content:center;border-right:1px solid #eef3f6;height:100%;overflow:hidden}' +
    '.fqv-media img{max-width:100%;max-height:100%;object-fit:contain;display:block}' +
    '.fqv-body{padding:22px 24px 0;display:flex;flex-direction:column;height:100%;min-height:0}' +
    '.fqv-t{margin:0 26px 0 0;font-size:16.5px;font-weight:700;line-height:1.3;color:#0C2D4A}' +
    '.fqv-sku{font-size:12px;color:#5c6f7e;margin-top:5px}' +
    '.fqv-sku b{font-weight:600;color:#0C2D4A}' +
    '.fqv-p{font-size:19px;font-weight:700;color:#0C2D4A;margin-top:8px;font-variant-numeric:tabular-nums}' +
    '.fqv-stock{display:inline-flex;align-items:center;gap:5px;font-size:11.5px;color:#0d7d68;margin-top:5px}' +
    '.fqv-stock i{width:6px;height:6px;border-radius:50%;background:#0d7d68;display:inline-block}' +
    /* the one scrolling region: specs + description, sized by what is left */
    '.fqv-scroll{flex:1 1 auto;min-height:0;overflow-y:auto;margin-top:10px;padding-right:8px;scrollbar-width:thin;scrollbar-color:#b9c6cf #eef2f5}' +
    '.fqv-scroll::-webkit-scrollbar{width:8px}' +
    '.fqv-scroll::-webkit-scrollbar-track{background:#eef2f5;border-radius:4px}' +
    '.fqv-scroll::-webkit-scrollbar-thumb{background:#b9c6cf;border-radius:4px}' +
    '.fqv-scroll:focus-visible{outline:2px solid #0d7d68;outline-offset:2px}' +
    /* v1.4: the lead sentence, above the specs, in the body ink */
    '.fqv-lead{font-size:12.5px;line-height:1.5;font-weight:500;color:#12222f;margin:0 0 10px}' +
    '.fqv-specs{margin:0;padding:0 0 0 18px;list-style:disc}' +
    '.fqv-specs li{font-size:12.5px;line-height:1.45;color:#12222f;margin:0 0 5px}' +
    '.fqv-specs li::marker{color:#c3cdd5}' +
    '.fqv-specs li b{font-weight:600;color:#0C2D4A}' +
    '.fqv-desc{font-size:12.5px;line-height:1.55;color:#5c6f7e;margin:10px 0 0}' +
    '.fqv-specs + .fqv-desc{margin-top:10px}' +
    '.fqv-link{display:inline-block;margin-top:10px;font-size:12.5px;font-weight:600;color:#0d7d68;text-decoration:none}' +
    '.fqv-link:hover{text-decoration:underline}' +
    '.fqv-buy{flex:0 0 auto;padding:12px 0 16px;background:#fff;border-top:1px solid #eef3f6}' +
    '.fqv-actions{display:flex;align-items:center;gap:10px}' +
    '.fqv-step{display:inline-flex;align-items:center;border:1px solid #e3e9ee;border-radius:7px;overflow:hidden}' +
    '.fqv-step button{width:28px;height:34px;border:0;background:#f7fafb;color:#12222f;font-size:15px;font-weight:600;cursor:pointer;font-family:inherit}' +
    '.fqv-step b{min-width:32px;height:34px;display:inline-flex;align-items:center;justify-content:center;background:#fff;font-size:13.5px;font-weight:600;font-variant-numeric:tabular-nums}' +
    '.fqv-add{flex:1 1 auto;border:0;border-radius:8px;background:#20D5B3;color:#062334;font-size:14px;font-weight:700;padding:11px 16px;cursor:pointer;font-family:inherit}' +
    '.fqv-add:hover{background:#1bc4a4}' +
    '.fqv-add:disabled{background:#e3e9ee;color:#8496a3;cursor:default}' +
    '.fqv-add.done{background:#0d7d68;color:#fff}' +
    '.fqv-quote{flex:1 1 auto;text-align:center;font-size:14px;font-weight:700;color:#0C2D4A;padding:11px 0}' +
    /* narrow screens: image on top, the detail column keeps its own scroll */
    '@media (max-width:620px){' +
      '.fqv-grid{grid-template-columns:1fr;height:auto}' +
      '.fqv-media{border-right:0;border-bottom:1px solid #eef3f6;height:190px;padding:18px}' +
      '.fqv-body{height:auto;padding:18px 20px 0}' +
      '.fqv-scroll{max-height:34vh}' +
    '}';
  document.head.appendChild(css);

  var byHandle = null;
  function loadIndex(cb) {
    if (byHandle) { cb(); return; }
    if (!INDEX_URL) { byHandle = {}; cb(); return; }
    fetch(INDEX_URL).then(function (r) { return r.json(); }).then(function (d) {
      byHandle = {};
      (d.products || []).forEach(function (p) { if (p && p.handle && !byHandle[p.handle]) byHandle[p.handle] = p; });
      cb();
    }).catch(function () { byHandle = {}; cb(); });
  }

  function isQuote(sku) { var s = String(sku || '').toUpperCase(); return s.indexOf('OSK') !== -1 || /^(EXK|MDA|MDB|MNA|MNB|MXA)-/.test(s); }
  function money(n) { return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
  function humanize(key) {
    return String(key).replace(/_/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }
  function clean(s) { return String(s || '').replace(/\s+/g, ' ').trim(); }

  /* v1.4: the first sentence = up to the first . ! or ? that is followed by
     a space or the end of the copy, so decimals ("0.45 um") and figures
     survive the split; copy with no sentence break leads with all of it */
  function firstSentence(s) {
    var txt = clean(s);
    if (!txt) return { head: '', rest: '' };
    var m = /^[\s\S]*?[.!?](?=\s|$)/.exec(txt);
    if (!m) return { head: txt, rest: '' };
    return { head: m[0].trim(), rest: txt.slice(m[0].length).trim() };
  }

  /* v1.0.2: bodies lead with the spec table - split it out instead of
     letting textContent glue "Specification Value ..." onto the prose */
  function splitBody(html) {
    var out = { desc: '', specs: [] };
    if (!html) return out;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    [].slice.call(doc.querySelectorAll('script, style')).forEach(function (n) { n.remove(); });
    [].slice.call(doc.querySelectorAll('table')).forEach(function (tbl) {
      [].slice.call(tbl.querySelectorAll('tr')).forEach(function (tr) {
        if (tr.querySelector('th') || (tr.parentElement && tr.parentElement.tagName === 'THEAD')) return;
        var cells = tr.children;
        if (!cells.length) return;
        var label = clean(cells[0].textContent);
        var value = cells.length > 1 ? clean(cells[1].textContent) : '';
        if (!label && !value) return;
        if (label === 'Specification' && value === 'Value') return;
        out.specs.push([label, value]);
      });
      tbl.remove();
    });
    out.desc = clean(doc.body.textContent);
    return out;
  }

  var opener = null;
  var FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';
  function veil() { return document.getElementById('fqv-veil'); }
  function close() {
    var v = veil();
    if (v) v.classList.remove('on');
    document.removeEventListener('keydown', onKey, true);
    if (opener && document.contains(opener)) { try { opener.focus(); } catch (e) {} }
    opener = null;
  }
  function onKey(e) {
    var v = veil();
    if (!v || !v.classList.contains('on')) return;
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      e.stopPropagation();   // Esc peels ONE layer - the drawer's own Esc listener must not also fire
      close();
      return;
    }
    if (e.key !== 'Tab') return;
    var f = [].slice.call(v.querySelectorAll(FOCUSABLE)).filter(function (el) {
      var r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0;
    });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1], a = document.activeElement;
    if (!v.contains(a)) { e.preventDefault(); first.focus(); return; }
    if (e.shiftKey && a === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && a === last) { e.preventDefault(); first.focus(); }
  }
  function ensureVeil() {
    var v = veil();
    if (v) return v;
    v = document.createElement('div');
    v.id = 'fqv-veil';
    v.addEventListener('click', function (e) { if (e.target === v) close(); });
    document.body.appendChild(v);
    return v;
  }

  function render(item, body) {
    var v = ensureVeil();
    v.innerHTML = '';
    var m = document.createElement('div');
    m.id = 'fqv';
    m.setAttribute('role', 'dialog');
    m.setAttribute('aria-modal', 'true');
    m.setAttribute('aria-label', 'Quick view: ' + (item.title || ''));
    var x = document.createElement('button');
    x.id = 'fqv-x';
    x.setAttribute('aria-label', 'Close quick view');
    x.innerHTML = '&times;';
    x.addEventListener('click', close);
    m.appendChild(x);
    var grid = document.createElement('div');
    grid.className = 'fqv-grid';
    var media = document.createElement('div');
    media.className = 'fqv-media';
    if (item.image) {
      var img = document.createElement('img');
      img.src = item.image;
      img.alt = item.title || '';
      media.appendChild(img);
    }
    grid.appendChild(media);

    var bodyEl = document.createElement('div');
    bodyEl.className = 'fqv-body';
    var t = document.createElement('h3');
    t.className = 'fqv-t';
    t.textContent = item.title || '';
    bodyEl.appendChild(t);
    if (item.sku) {
      var sk = document.createElement('div');
      sk.className = 'fqv-sku';
      sk.appendChild(document.createTextNode('SKU: '));
      var b = document.createElement('b');
      b.textContent = item.sku;
      sk.appendChild(b);
      bodyEl.appendChild(sk);
    }
    var priceN = parseFloat(item.price);
    var p = document.createElement('div');
    p.className = 'fqv-p';
    p.textContent = isQuote(item.sku) ? 'Call for Quote' : (isFinite(priceN) ? money(priceN) : '');
    bodyEl.appendChild(p);
    if (item.available) {
      var st = document.createElement('span');
      st.className = 'fqv-stock';
      st.innerHTML = '<i></i>In stock';
      bodyEl.appendChild(st);
    }

    /* everything below scrolls inside the card */
    var scroll = document.createElement('div');
    scroll.className = 'fqv-scroll';
    scroll.setAttribute('role', 'region');
    scroll.setAttribute('aria-label', 'Product details');
    scroll.setAttribute('tabindex', '0');
    /* v1.4: lead sentence first - visible without scrolling */
    var split = firstSentence(body && body.desc);
    if (split.head) {
      var lead = document.createElement('p');
      lead.className = 'fqv-lead';
      lead.textContent = split.head;
      scroll.appendChild(lead);
    }
    var pairs = [];
    var specs = item.specs || {};
    Object.keys(specs).forEach(function (k) {
      var val = specs[k];
      if (typeof val === 'boolean') val = val ? 'Yes' : 'No';
      if (val === '' || val === null || val === undefined) return;
      pairs.push([humanize(k), String(val)]);
    });
    if (!pairs.length && body && body.specs.length) pairs = body.specs;
    if (pairs.length) {
      var ul = document.createElement('ul');
      ul.className = 'fqv-specs';
      pairs.forEach(function (pair) {
        var li = document.createElement('li');
        if (pair[0] && pair[1]) {
          var lb = document.createElement('b');
          lb.textContent = pair[0] + ': ';
          li.appendChild(lb);
          li.appendChild(document.createTextNode(pair[1]));
        } else {
          li.textContent = pair[0] || pair[1];
        }
        ul.appendChild(li);
      });
      scroll.appendChild(ul);
    }
    if (split.rest) {
      var dd = document.createElement('p');
      dd.className = 'fqv-desc';
      dd.textContent = split.rest;
      scroll.appendChild(dd);
    }
    var link = document.createElement('a');
    link.className = 'fqv-link';
    link.href = '/products/' + item.handle;
    link.textContent = 'View full details →';
    scroll.appendChild(link);
    bodyEl.appendChild(scroll);

    var buy = document.createElement('div');
    buy.className = 'fqv-buy';
    var act = document.createElement('div');
    act.className = 'fqv-actions';
    if (item.vid && !isQuote(item.sku) && item.available) {
      var qty = 1;
      var step = document.createElement('div');
      step.className = 'fqv-step';
      var dec = document.createElement('button');
      dec.type = 'button'; dec.setAttribute('aria-label', 'Decrease quantity'); dec.textContent = '−';
      var num = document.createElement('b');
      num.setAttribute('aria-live', 'polite'); num.textContent = '1';
      var inc = document.createElement('button');
      inc.type = 'button'; inc.setAttribute('aria-label', 'Increase quantity'); inc.textContent = '+';
      dec.addEventListener('click', function () { if (qty > 1) { qty--; num.textContent = qty; } });
      inc.addEventListener('click', function () { if (qty < 99) { qty++; num.textContent = qty; } });
      step.appendChild(dec); step.appendChild(num); step.appendChild(inc);
      act.appendChild(step);
      var add = document.createElement('button');
      add.type = 'button';
      add.className = 'fqv-add';
      add.textContent = 'Add to cart';
      add.addEventListener('click', function () {
        add.disabled = true;
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: Number(item.vid), quantity: qty })
        }).then(function (r) {
          if (!r.ok) { var err = new Error('add failed'); err.soldOut = r.status === 422; throw err; }
          add.classList.add('done');
          add.textContent = 'Added ✓';
          setTimeout(function () { add.classList.remove('done'); add.textContent = 'Add to cart'; add.disabled = false; }, 1600);
          if (window.__fcartRefresh) window.__fcartRefresh();
          return fetch('/cart.js').then(function (cr) { return cr.json(); }).then(function (c) {
            [].slice.call(document.querySelectorAll('.fh3-cart .count, .fv3-cart .count, .fv3-actions .count')).forEach(function (el) { el.textContent = c.item_count; });
          });
        }).catch(function (err) {
          if (err && err.soldOut) {   // v1.5: 422 is the cart saying no, not a blip
            add.textContent = 'Sold out';
            add.disabled = true;
            return;
          }
          add.textContent = 'Try again';
          add.disabled = false;
        });
      });
      act.appendChild(add);
    } else if (isQuote(item.sku)) {
      var q = document.createElement('div');
      q.className = 'fqv-quote';
      q.textContent = 'Call for Quote · (858) 224-9100';
      act.appendChild(q);
    } else {
      /* v1.5: never leave the buy row empty - say why there is no add */
      var so = document.createElement('button');
      so.type = 'button';
      so.className = 'fqv-add';
      so.disabled = true;
      so.textContent = item.available ? 'Unavailable' : 'Sold out';
      act.appendChild(so);
    }
    buy.appendChild(act);
    bodyEl.appendChild(buy);

    grid.appendChild(bodyEl);
    m.appendChild(grid);
    v.appendChild(m);
    v.classList.add('on');
    document.addEventListener('keydown', onKey, true);
    try { x.focus({ preventScroll: true }); } catch (e) { x.focus(); }
  }

  function open(handle, openerEl) {
    if (!handle) return;
    opener = openerEl || (document.activeElement !== document.body ? document.activeElement : null);
    loadIndex(function () {
      var item = byHandle[handle];
      if (!item) { window.location.href = '/products/' + handle; return; }
      var bodyP = fetch('/products/' + handle + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) { return splitBody(j && j.description); })
        .catch(function () { return null; });
      Promise.race([bodyP, new Promise(function (res) { setTimeout(function () { res(null); }, 1200); })])
        .then(function (body) { render(item, body); });
    });
  }

  /* decoupled triggers: any [data-fqv-handle] click opens the modal; capture
     phase + preventDefault so triggers nested inside product links (the
     rail-card bars) never navigate */
  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target.closest('[data-fqv-handle]') : null;
    if (!t) return;
    e.preventDefault();
    e.stopPropagation();
    open(t.getAttribute('data-fqv-handle'), t);
  }, true);

  window.FV3_QUICKVIEW = { open: open, close: close };
})();
